/**
 * Journal Explanations — Wave E.
 *
 * For every posted journal entry, derive a one-sentence plain-English
 * rationale + a confidence score that reflects how cleanly the entry was
 * generated. Deterministic — uses entry source + line shape, no LLM.
 *
 * Surfacing pattern:
 *   const { explanation, confidence, factors } = explainJournalEntry(je, accounts);
 *
 * Confidence ranges:
 *   • 95–100  System-generated, deterministic source (invoice, payment, payroll)
 *   • 80–94   Auto-posted but with at least one fuzzy resolution (e.g. job memo bucketed by substring)
 *   • 60–79   Manual entry with full memos + balanced lines
 *   • <60     Manual + missing memos / round-dollar / weekend / anomaly-flagged
 */
import type { JournalEntry, GLAccount } from "./glTypes";

export interface JournalExplanation {
  /** One-sentence plain-English rationale. */
  explanation: string;
  /** 0–100, higher = system more sure this entry is correct as posted. */
  confidence: number;
  /** What pushed confidence up or down. */
  factors: Array<{ label: string; delta: number }>;
}

const CENT = (n: number) => Math.round(n * 100);

function totalDebit(je: JournalEntry): number {
  return je.lines.filter(l => l.side === "debit").reduce((s, l) => s + l.amount, 0);
}

function accName(id: string, accounts: GLAccount[]): string {
  const a = accounts.find(x => x.id === id);
  return a ? `${a.number} ${a.name}` : id;
}

function debitAccount(je: JournalEntry): string | undefined {
  return je.lines.find(l => l.side === "debit")?.accountId;
}
function creditAccount(je: JournalEntry): string | undefined {
  return je.lines.find(l => l.side === "credit")?.accountId;
}

const fmt$ = (n: number) =>
  `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function explainJournalEntry(
  je: JournalEntry,
  accounts: GLAccount[],
): JournalExplanation {
  const amount = totalDebit(je);
  const dr = debitAccount(je);
  const cr = creditAccount(je);
  const drName = dr ? accName(dr, accounts) : "—";
  const crName = cr ? accName(cr, accounts) : "—";

  let explanation = je.description;
  let confidence = 90;
  const factors: Array<{ label: string; delta: number }> = [];

  switch (je.source) {
    case "ar_invoice":
      explanation = `Customer invoice issued for ${fmt$(amount)} — receivable booked against deferred revenue until the job completes.`;
      confidence = 98;
      factors.push({ label: "Deterministic AR posting rule", delta: +8 });
      break;
    case "ar_cash_receipt":
      explanation = `Cash receipt of ${fmt$(amount)} applied — ${crName} cleared into ${drName}.`;
      confidence = 97;
      factors.push({ label: "Direct payment → cash mapping", delta: +7 });
      break;
    case "ap_bill":
      explanation = `Vendor bill for ${fmt$(amount)} booked — cost lands on ${drName.split(" ").slice(1).join(" ") || "expense"} and AP rises.`;
      confidence = 92;
      factors.push({ label: "Operator-chosen account on bill line", delta: +2 });
      break;
    case "ap_payment":
      explanation = `Vendor payment of ${fmt$(amount)} — AP cleared from ${crName}.`;
      confidence = 96;
      factors.push({ label: "Deterministic AP-clear rule", delta: +6 });
      break;
    case "payroll":
      explanation = `Pay run released — labor cost of ${fmt$(amount)} capitalized to WIP and paid from payroll cash.`;
      confidence = 95;
      factors.push({ label: "Deterministic payroll rule", delta: +5 });
      break;
    case "wip_release":
      explanation = `Job complete — deferred revenue recognized and WIP released to COGS.`;
      confidence = 88;
      factors.push({ label: "Material/labor split inferred from prior memos", delta: -2 });
      break;
    case "bank_feed":
      explanation = `Bank feed reconciliation — ${fmt$(amount)} matched from imported transactions.`;
      confidence = 90;
      break;
    case "sales_tax":
      explanation = `Sales tax remittance — ${fmt$(amount)} cleared from the sales-tax liability.`;
      confidence = 96;
      break;
    case "adjustment":
      explanation = je.description || `Adjusting entry of ${fmt$(amount)} — ${drName} / ${crName}.`;
      confidence = 78;
      factors.push({ label: "Adjusting entry — review classification", delta: -10 });
      break;
    case "manual":
    default:
      explanation = je.description
        ? `Manual entry — ${je.description}.`
        : `Manual entry of ${fmt$(amount)} between ${drName} and ${crName}.`;
      confidence = 72;
      factors.push({ label: "Manual posting — no automatic source", delta: -18 });
      break;
  }

  // ── Confidence modifiers (shared across all sources) ────────────────────

  // Memo quality
  const missingMemoLines = je.lines.filter(l => !l.memo || !l.memo.trim()).length;
  if (missingMemoLines > 0 && amount >= 500) {
    const delta = Math.min(-15, -3 * missingMemoLines);
    confidence += delta;
    factors.push({ label: `${missingMemoLines} line${missingMemoLines === 1 ? "" : "s"} missing memo`, delta });
  }

  // Round-dollar (placeholder smell) on manual / adjustment entries
  if ((je.source === "manual" || je.source === "adjustment") &&
      amount >= 1000 && CENT(amount) % CENT(500) === 0) {
    confidence -= 8;
    factors.push({ label: "Round-dollar amount", delta: -8 });
  }

  // Weekend post on a manual entry — operator might have caught up sloppily
  const [y, m, d] = je.postingDate.slice(0, 10).split("-").map(Number);
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  if ((weekday === 0 || weekday === 6) && je.source === "manual") {
    confidence -= 4;
    factors.push({ label: "Weekend-dated manual entry", delta: -4 });
  }

  // Already voided — readers should see this immediately
  if (je.status === "void") {
    confidence = Math.min(confidence, 50);
    factors.push({ label: "Entry has been voided", delta: 0 });
  }

  // Subledger gaps — entries against AR/AP without a customer/vendor tag
  const arApLines = je.lines.filter(l => l.accountId === "1100" || l.accountId === "2000");
  const ungated = arApLines.filter(l => !l.customerId && !l.vendorId).length;
  if (ungated > 0) {
    confidence -= 6;
    factors.push({ label: "AR/AP line missing subledger tag", delta: -6 });
  }

  confidence = Math.max(0, Math.min(100, Math.round(confidence)));
  return { explanation, confidence, factors };
}
